import { create } from 'zustand';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { COLLECTIONS } from '../services/firestore';

const normalizeLearner = (snapshot) => {
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    ...data,
    grade: String(data.grade || '').trim(),
    subjects: Array.isArray(data.subjects) ? data.subjects : [],
    createdAt: data.createdAt?.toDate?.() || null,
    updatedAt: data.updatedAt?.toDate?.() || null,
  };
};

const sortLearners = (learners) =>
  [...learners].sort((a, b) => {
    const surnameA = String(a.surname || '').toLowerCase();
    const surnameB = String(b.surname || '').toLowerCase();
    if (surnameA !== surnameB) return surnameA.localeCompare(surnameB);
    const nameA = String(a.name || '').toLowerCase();
    const nameB = String(b.name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });

export const useLearnersStore = create((set) => ({
  learners: [],
  isLoading: false,
  isSaving: false,
  error: null,

  loadLearners: async (userId) => {
    try {
      set({ isLoading: true, error: null });
      const learnersQuery = query(
        collection(db, COLLECTIONS.LEARNERS),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(learnersQuery);
      const learners = sortLearners(snapshot.docs.map(normalizeLearner));
      set({ learners, isLoading: false });
      return learners;
    } catch (error) {
      console.error('Error loading learners:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  createLearner: async (learnerData) => {
    try {
      set({ isSaving: true, error: null });
      const docRef = await addDoc(collection(db, COLLECTIONS.LEARNERS), {
        ...learnerData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const createdLearner = {
        id: docRef.id,
        ...learnerData,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      set((state) => ({
        learners: sortLearners([createdLearner, ...state.learners]),
        isSaving: false,
      }));
      return createdLearner;
    } catch (error) {
      console.error('Error creating learner:', error);
      set({ error: error.message, isSaving: false });
      throw error;
    }
  },

  batchCreateLearners: async (learnersData = []) => {
    try {
      if (!Array.isArray(learnersData) || learnersData.length === 0) return [];
      set({ isSaving: true, error: null });

      const batch = writeBatch(db);
      const localNow = new Date();
      const createdLearners = [];

      learnersData.forEach((learnerData) => {
        const learnerRef = doc(collection(db, COLLECTIONS.LEARNERS));
        batch.set(learnerRef, {
          ...learnerData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        createdLearners.push({
          id: learnerRef.id,
          ...learnerData,
          createdAt: localNow,
          updatedAt: localNow,
        });
      });

      await batch.commit();

      set((state) => ({
        learners: sortLearners([...createdLearners, ...state.learners]),
        isSaving: false,
      }));
      return createdLearners;
    } catch (error) {
      console.error('Error batch creating learners:', error);
      set({ error: error.message, isSaving: false });
      throw error;
    }
  },

  updateLearner: async (learnerId, updates) => {
    try {
      set({ isSaving: true, error: null });
      await updateDoc(doc(db, COLLECTIONS.LEARNERS, learnerId), {
        ...updates,
        updatedAt: serverTimestamp(),
      });
      set((state) => ({
        learners: sortLearners(
          state.learners.map((learner) =>
            learner.id === learnerId
              ? {
                  ...learner,
                  ...updates,
                  updatedAt: new Date(),
                }
              : learner
          )
        ),
        isSaving: false,
      }));
    } catch (error) {
      console.error('Error updating learner:', error);
      set({ error: error.message, isSaving: false });
      throw error;
    }
  },

  deleteLearner: async (learnerId) => {
    try {
      set({ error: null });
      await deleteDoc(doc(db, COLLECTIONS.LEARNERS, learnerId));
      set((state) => ({
        learners: state.learners.filter((learner) => learner.id !== learnerId),
      }));
    } catch (error) {
      console.error('Error deleting learner:', error);
      set({ error: error.message });
      throw error;
    }
  },

  clearLearnersError: () => set({ error: null }),
}));
